import {
  getInitialManageBoardFormValues,
  ManageBoardForm,
  ManageBoardFormValues,
  transformFormValuesToBoardData,
} from "@jugl-web/domain-resources/tasks/components/ManageBoardForm";
import {
  NavigationGuardProvider,
  useNavigationGuardContext,
} from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { Button } from "@jugl-web/ui-components";
import { Alert } from "@jugl-web/ui-components/cross-platform/Alert";
import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import { cx, isAPIError, useToast, useTranslations } from "@jugl-web/utils";
import { useThrottledSubmit } from "@jugl-web/utils/form/hooks/useThrottledSubmit";
import { forwardRef, useEffect, useId, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { ReactComponent as BoardIcon } from "./assets/board.svg";

interface NewBoardDialogProps {
  entityId: string;
  meId: string;
  onOpenStateChange?: (isOpen: boolean) => void;
  onAfterCreate?: (boardId: string) => void;
}

export interface NewBoardDialogHandle {
  open: () => void;
}

const NewBoardDialogInner = forwardRef<
  NewBoardDialogHandle,
  NewBoardDialogProps
>(({ entityId, meId, onOpenStateChange, onAfterCreate }, ref) => {
  const [openedDialog, setOpenedDialog] = useState<"basic" | "advanced" | null>(
    null
  );

  const isOpen = openedDialog !== null;

  const { tasksApi } = useRestApiProvider();

  const [createBoard, { isLoading }] = tasksApi.useCreateTaskBoardMutation();

  const formId = useId();
  const form = useForm<ManageBoardFormValues>({
    defaultValues: getInitialManageBoardFormValues(meId),
  });

  const { registerDirtySource, requestNavigation, reset } =
    useNavigationGuardContext();

  const { toast } = useToast();
  const { t } = useTranslations();

  useImperativeHandle(ref, () => ({
    open: () => setOpenedDialog("basic"),
  }));

  const handleSubmit = form.handleSubmit(async (data) => {
    const transformedBoardData = transformFormValuesToBoardData(data);

    const boardUsersWithoutMe = transformedBoardData.users.filter(
      (user) => user.id !== meId
    );

    const response = await createBoard({
      entityId,
      data: {
        ...transformedBoardData,
        users: boardUsersWithoutMe,
      },
    });

    if ("data" in response) {
      toast(
        t({
          id: "feedback.board-created",
          defaultMessage: "Board has been created",
        })
      );

      reset();
      onAfterCreate?.(response.data.id);
      setOpenedDialog(null);
    } else if (isAPIError(response.error) && response.error.status === 400) {
      const apiErrors = response.error.data?.errors;
      const prefixError =
        Array.isArray(apiErrors) &&
        apiErrors.find((err) => err.message === "prefix_already_exists");

      if (prefixError) {
        form.setError("prefix", {
          message: t({
            id: "tasks-page.board-with-that-prefix-already-exists",
            defaultMessage: "Board with that prefix already exists",
          }),
        });
      } else {
        form.setError("name", {
          message: t({
            id: "tasks-page.board-with-that-name-already-exists",
            defaultMessage: "Board with that name already exists",
          }),
        });
      }
    }
  });

  const handleSubmitThrottled = useThrottledSubmit(handleSubmit);

  const handleSafeClose = () => {
    requestNavigation(() => {
      reset();
      setOpenedDialog(null);
    });
  };

  useEffect(() => {
    if (isOpen) {
      form.reset();
      window.setTimeout(() => {
        form.setFocus("name");
      }, 100);
    }
  }, [form, isOpen]);

  useEffect(() => {
    onOpenStateChange?.(isOpen);
  }, [isOpen, onOpenStateChange]);

  useEffect(() => {
    registerDirtySource("new-board-dialog", form.formState.isDirty);
  }, [registerDirtySource, form.formState.isDirty]);

  return (
    <>
      {/* BASIC FORM */}
      <Alert
        className="w-[580px]"
        isOpen={openedDialog === "basic"}
        onRequestClose={handleSafeClose}
        isCloseButtonVisible
        contentContainerClassName="py-8 px-10"
        buttonsContainerClassName="mt-8"
        header={<NewBoardDialogHeader size="md" />}
        buttons={[
          {
            type: "submit",
            form: formId,
            className: "w-[200px] h-10 mx-auto font-secondary font-medium",
            isDisabled: isLoading,
            text: t({
              id: "tasks-page.create-board",
              defaultMessage: "Create Board",
            }),
            color: "primary",
          },
        ]}
        content={
          <ManageBoardForm
            entityId={entityId}
            meId={meId}
            formId={formId}
            form={form}
            mode={{
              type: "basic",
              onTriggerAdvancedMode: () => setOpenedDialog("advanced"),
            }}
            className="text-left"
            onSubmit={handleSubmitThrottled}
          />
        }
      />
      {/* ADVANCED FORM */}
      <BottomCenteredDrawer
        isOpen={openedDialog === "advanced"}
        header={{
          type: "title",
          title: <NewBoardDialogHeader size="lg" />,
        }}
        size="fullscreen"
        onClose={handleSafeClose}
      >
        <BottomCenteredDrawer.Content className="flex justify-center px-4 pt-10">
          <ManageBoardForm
            entityId={entityId}
            meId={meId}
            formId={formId}
            form={form}
            mode={{ type: "advanced" }}
            className="text-left"
            onSubmit={handleSubmitThrottled}
          />
        </BottomCenteredDrawer.Content>
        <BottomCenteredDrawer.Actions>
          <Button
            type="submit"
            form={formId}
            variant="contained"
            color="primary"
            isDisabled={isLoading}
            className="font-secondary h-10 w-[220px] font-medium"
          >
            {t({
              id: "tasks-page.create-board",
              defaultMessage: "Create Board",
            })}
          </Button>
        </BottomCenteredDrawer.Actions>
      </BottomCenteredDrawer>
    </>
  );
});

export const NewBoardDialog = forwardRef<
  NewBoardDialogHandle,
  NewBoardDialogProps
>((props, ref) => (
  <NavigationGuardProvider>
    <NewBoardDialogInner {...props} ref={ref} />
  </NavigationGuardProvider>
));

const NewBoardDialogHeader = ({ size }: { size: "md" | "lg" }) => {
  const { t } = useTranslations();

  return (
    <div className="flex items-center gap-3">
      <BoardIcon />
      <span
        className={cx(
          "font-secondary font-semibold text-[#383838]",
          size === "md" ? "text-sm" : "text-base"
        )}
      >
        {t({ id: "tasks-page.new-board", defaultMessage: "New Board" })}
      </span>
    </div>
  );
};
